<template>
    <div class="landing-page-tabs">
        <div class="landing-page-tabs__titles-container">
            <div class="landing-page-tabs -wrap">
                <div class="landing-page-tabs__titles-container-wrapper">
                    <div :class="getTabClass(tab)" @click="onTabClick(tab)" :key='tab.id' v-for="tab in tabs" :data-text="tab.title">
                        {{ tab.title }}
                    </div>
                </div>
            </div>
        </div>
        <DigitalClassroomsUpdateBanner v-if="shouldShowLiveNotificationBanner"/>
        <CloudProLearningCoachBanner v-if="shouldShowLearningCoachBanner('fi-cloudpro')"/>
        <CloudSkillsLearningCoachBanner v-if="shouldShowLearningCoachBanner('fi-cloudskills') && !hasUserCloudProPackage"/>
        <TaxationDayBanner v-if="shouldShowTaxationDayBanner"/>
        <div class="landing-page-tabs -wrap">
            <div class="tab-content">
                <slot :onTabMounted="onTabMounted" :activeTab="activeTab"></slot>
            </div>
        </div>
    </div>
</template>

<script>
    import DigitalClassroomsUpdateBanner from 'components/landingpage/DigitalClassRoomsUpdateBanner'
    import CloudProLearningCoachBanner from './CloudProLearningCoachBanner'
    import CloudSkillsLearningCoachBanner from './CloudSkillsLearningCoachBanner'
    import TaxationDayBanner from 'components/landingpage/TaxationDayBanner'
    import { mapGetters } from 'vuex'
    import dateHelper from 'components/shared/mixins/dateHelper'
    import moment from 'moment'
    import config from 'services/config'

    export default {
        components: { DigitalClassroomsUpdateBanner, CloudProLearningCoachBanner, CloudSkillsLearningCoachBanner, TaxationDayBanner },
        mixins: [ dateHelper ],
        data () {
            return {
                tabs: [],
                activeTab: null
            }
        },
        provide: function () {
            return {
                activateTab: this.activateTab
            }
        },
        computed: {
            ...mapGetters({
                userAccessablePackages: 'User/getUserAccessablePackages',
                language: 'Languages/getSelectedCountryCode'
            }),
            shouldShowLiveNotificationBanner () {
                const relatedPackageCodes = ['fi-business', 'fi-total', 'fi-public']
                const doesUserHaveRelatedPackage = this.userAccessablePackages.some(userPackage => relatedPackageCodes.includes(userPackage.code))
                const now = moment().unix()
                const showLiveEventsNotifyBanner = window.localStorage.getItem('showLiveEventsNotifyBanner') || true
                return showLiveEventsNotifyBanner === true &&
                    now < config.februaryDigitalClassroomsUpdateBannerEndDate &&
                    doesUserHaveRelatedPackage && config.appLanguage === 'fi' && this.language === 'fi'
            },
            shouldShowTaxationDayBanner () {
                switch (config.appLanguage) {
                case 'se':
                    const showTaxationDayBanner = window.localStorage.getItem('showTaxationDayBanner2023') || true
                    const now = this.getUnixTimeStampByTimeZone()
                    const shouldBannerBeVisibleInSweden = now > config.taxationDaySwedenStart &&
                        now < config.taxationDaySwedenEnd
                    return (this.language === 'se' || this.language === 'en') && showTaxationDayBanner === true && shouldBannerBeVisibleInSweden
                }
                return false
            },
            hasUserCloudProPackage () {
                return this.userAccessablePackages.some(userPackage => userPackage.code === 'fi-cloudpro')
            }
        },
        methods: {
            onTabClick (tab) {
                this.activeTab = tab
            },
            activateTab (tabId) {
                this.activeTab = this.tabs.find(tab => tab.id === tabId)
            },
            onTabMounted (tab) {
                if (!this.activeTab) {
                    this.activeTab = tab
                }
                this.tabs.push(tab)
            },
            getTabClass (tab) {
                const tabClass = ['landing-page-tabs__tab-title']
                if (tab.id === this.activeTab.id) {
                    tabClass.push('landing-page-tabs__tab-title--active')
                }
                return tabClass
            },
            shouldShowLearningCoachBanner (packageCode) {
                const doesUserHaveRelatedPackage = this.userAccessablePackages.some(userPackage => userPackage.code === packageCode)
                return doesUserHaveRelatedPackage && config.appLanguage === 'fi' && this.language === 'fi'
            }
        }
    }
</script>

<style lang="scss">
.landing-page-tabs {
    @mixin transiton {
        transition: background-color 0.3s;
    }

    @mixin after-bar {
        bottom: 0;
        content: '';
        height: 3px;
        position: absolute;
        width: 100%;
    }

    align-items: center;
    position: relative;

    [class*="desktop"] & {
        top: -23px;
    }

    &__titles-container {
        position: relative;

        [class*="mobile"] & {
            overflow-x: auto;

            &::-webkit-scrollbar {
                display: none;
            }
            &::after {
                @include after-bar;
                background-color: $silver_chalice;
                z-index: 0;
            }
        }

        [class*="desktop"] & {
            z-index: 2;
        }
    }

    &__titles-container-wrapper {
        display: flex;
    }

    &__tab-title {
        @include transiton;
        cursor: pointer;
        font-size: 1.8rem;
        padding: 13px 25px;
        position: relative;
        color: $black;
        white-space: nowrap;

        [class*="mobile"] & {
            font-family: Nunito-Bold, sans-serif;
            padding: 20px;

            &--active,
            &:hover {
                color: $mai_tai;
            }

            &::after,
            &:hover::after {
                @include after-bar;
                @include transiton;
                background-color: $mai_tai;
                left: 50%;
                transform: translateX(-50%);
                width: calc(100% - 40px);
                z-index: 1;
            }

            &--active::after {
                width: 100% !important;
            }

            &::after {
                background-color: $silver_chalice;
                width: 100% !important;
            }
        }

        [class*="desktop"] & {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 5px 5px 0 0;
            margin-right: 4px;
            color: transparent;
            font-family: Nunito-ExtraBold, sans-serif;

            &:hover {
                color: transparent;
                background-color: $white;
            }

            &:hover:after {
                font-family: Nunito-ExtraBold, sans-serif;
                color: $mai_tai;
            }

            &:after {
                content: attr(data-text);
                position: absolute;
                left: 25px;
                font-family: Nunito-Bold, sans-serif;
                letter-spacing: 0.02rem;
                color: $white;
            }

            &--active {
                color: transparent;
                background-color: $white;
                font-family: Nunito-ExtraBold, sans-serif;

                &:after {
                    font-family: Nunito-ExtraBold, sans-serif;
                    color: $mai_tai;
                }
            }
        }
    }
}
</style>
